import { graphHooks } from '@tunasong/graph-lib/react'
import type { Agent, Comment, Persisted } from '@tunasong/schemas'
import { isAgent } from '@tunasong/schemas'
import { useMemo } from 'react'
import { isPendingProposal, isProposal, type ProposalComment } from './proposal-resolve.js'

/**
 * Internal: agent children of `hostId`. Inlined to keep this plumbing
 * PR free of any UX-chain dependency. The presence-selector PR
 * introduces a public `useAgentChildren` wrapper around the same
 * underlying cache query; this hook can collapse to that wrapper once
 * both PRs are on main.
 */
const useHostAgents = (hostId?: string | null): Persisted<Agent>[] => {
  const { entities } = graphHooks.useEntityChildren({
    parentId: hostId ?? undefined,
    filter: 'agent',
    skip: !hostId,
  })
  return useMemo(() => entities.filter(isAgent) as Persisted<Agent>[], [entities])
}

/**
 * Maximum number of agents per host the aggregating hooks
 * (`useHostProposals`, `useProposalsByBlock`) will resolve in a single
 * render. Beyond this cap, later agents' proposals are silently
 * excluded from the aggregate.
 *
 * Reasoning: React's rules-of-hooks require a stable hook-call count
 * across renders, so the aggregator pre-allocates N `useAgentProposals`
 * slots. Typical host has 1-3 agents; 8 is comfortable headroom. If a
 * host genuinely exceeds 8 agents we should move to a Redux-selector
 * aggregator (one hook reading flat cache state); deferred until that
 * scale becomes real.
 */
const MAX_AGENTS_AGGREGATED = 8

/**
 * Pending proposals for one specific agent. Powers the panel aggregate
 * count ("Proposals (n)") and the per-agent list view.
 *
 * Reads the same cached Comments as `ProposalList`; no new fetch. The
 * filter is `isProposal` ∩ `isPendingProposal` — resolved proposals
 * (accepted / rejected) are excluded from "pending" surfaces; they
 * remain in the panel's full history.
 */
export const useAgentProposals = (agentId?: string | null): ProposalComment[] => {
  const { entities: children } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agentId ?? undefined,
    skip: !agentId,
  })
  return useMemo(
    () => (children as Persisted<Comment>[]).filter(isProposal).filter(isPendingProposal),
    [children]
  )
}

/**
 * Pending proposals across ALL agents attached to a host entity (up to
 * `MAX_AGENTS_AGGREGATED`). Powers the rail-level total count and the
 * side panel's cross-agent view.
 *
 * Implementation note: React's rules-of-hooks forbid dynamic-length
 * hook chains, so the aggregator pre-allocates a fixed array of
 * `useAgentProposals` slots and feeds each agent's id into one. Slots
 * past the agent count are called with `undefined`, which short-circuits
 * inside `useAgentProposals` to an empty array.
 */
export const useHostProposals = (hostId?: string | null): ProposalComment[] => {
  const agents = useHostAgents(hostId)
  // Pre-allocated hook slots. Stable count = MAX_AGENTS_AGGREGATED.
  const slot0 = useAgentProposals(agents[0]?.id)
  const slot1 = useAgentProposals(agents[1]?.id)
  const slot2 = useAgentProposals(agents[2]?.id)
  const slot3 = useAgentProposals(agents[3]?.id)
  const slot4 = useAgentProposals(agents[4]?.id)
  const slot5 = useAgentProposals(agents[5]?.id)
  const slot6 = useAgentProposals(agents[6]?.id)
  const slot7 = useAgentProposals(agents[7]?.id)
  return useMemo(
    () => [...slot0, ...slot1, ...slot2, ...slot3, ...slot4, ...slot5, ...slot6, ...slot7],
    [slot0, slot1, slot2, slot3, slot4, slot5, slot6, slot7]
  )
}

/**
 * Build a Map<blockId, ProposalComment[]> from all host-wide pending
 * proposals. Each gutter chip reads its own bucket in O(1).
 *
 * Recomputed when the underlying agents/comments change — RTK Query +
 * graph-cache subscriptions invalidate the upstream `useAgentChildren`
 * + `useEntityChildren` queries. React Compiler memoizes the map.
 *
 * Proposals whose targetEntityId is not `hostId` are excluded from the
 * map: the gutter chip is for in-host blocks only. Such proposals still
 * surface in the panel via `useHostProposals` / `useAgentProposals`.
 */
export const useProposalsByBlock = (hostId?: string | null): Map<string, ProposalComment[]> => {
  const all = useHostProposals(hostId)
  return useMemo(() => {
    const map = new Map<string, ProposalComment[]>()
    for (const proposal of all) {
      const { anchorBlockId, targetEntityId } = proposal.metadata ?? {}
      if (!anchorBlockId || !targetEntityId || targetEntityId !== hostId) {
        continue
      }
      const bucket = map.get(anchorBlockId)
      if (bucket) {
        bucket.push(proposal)
      } else {
        map.set(anchorBlockId, [proposal])
      }
    }
    return map
  }, [all, hostId])
}

/**
 * Pending proposals anchored to a specific block. Powers the gutter
 * chip on the target block.
 *
 * Sugar over `useProposalsByBlock(hostId).get(blockId) ?? []`. The same
 * map serves every block on the host; per-block calls are O(1).
 */
export const useBlockProposals = (
  hostId: string | null | undefined,
  blockId: string | null | undefined
): ProposalComment[] => {
  const byBlock = useProposalsByBlock(hostId)
  if (!blockId) {
    return []
  }
  return byBlock.get(blockId) ?? []
}

/**
 * Convenience: pending proposals for an `Agent` entity. Same shape as
 * `useAgentProposals(agentId)` but takes the agent object directly. Lets
 * the agent card render the per-agent count without redundant id reads.
 */
export const useAgentProposalsForAgent = (agent: Persisted<Agent>): ProposalComment[] =>
  useAgentProposals(agent.id)

// Re-export the cap so the UX layer can render an overflow indicator
// when host has more than MAX_AGENTS_AGGREGATED agents (future work).
export { MAX_AGENTS_AGGREGATED }
